import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Alert, Spin } from 'antd';

import { marked } from 'marked';

import styled from 'styled-components';
import { accent } from 'services/careerAccents';
import { appLanguage } from 'services/appLanguage';
import { careerLayout, careerType } from 'services/careerType';
import { desktop } from 'services/ui';
import { sanitizeRichText } from 'services/SanitizeService';
import { Colors } from 'context';
import { CareerIcon, CareerIconName, VideoPlayer } from 'atoms';
import { CareerBookmarkButton, CareerLessonNotes, CareerLinkCard } from 'molecules';
import {
  CareerLockedSection,
  CareerExerciseForm,
  CareerExerciseResult,
  CareerLessonComplete,
  CareerLessonQuiz,
} from 'organisms';
import {
  authState,
  formatAttachmentSize,
  useCareerLesson,
  useCareerLessons,
  useCareerSectionAccess,
  useMarkCareerLessonOpened,
  useMyCareerCenter,
} from 'core';
import { lessonHref } from 'router/careerLinks';
import { PATHS } from 'router/config';

/**
 * /career/learning/:lessonId — a single career lesson (spec 0018 §6).
 *
 * The lock is entirely server-side: for a locked lesson the backend returns
 * `content: null`, `videoUrl: null`, `attachments: []` and this page renders
 * the teaser + a lock notice from `isUnlocked` alone. There is no client-side
 * unlock rule anywhere — completing the previous lesson is what flips the flag.
 */
export const CareerLesson = memo(() => {
  const { t } = useTranslation();
  const { lessonId } = useParams();
  const { active_role } = useReactiveVar(authState);

  // ТЗ 0069: членство больше не выбрасывает со страницы. Запрос урока
  // пропускается внутри `useCareerLesson`, поэтому здесь остаётся только
  // выбрать, что показать вместо него.
  const { isPreview } = useMyCareerCenter();
  const { locked, pending } = useCareerSectionAccess('lessons');

  const { lesson, loading, error, refetch } = useCareerLesson(lessonId || '');
  // Список нужен ради «следующий урок» после завершения. Тот же запрос, что у
  // экрана обучения: Apollo отдаёт его из кеша.
  const { lessons } = useCareerLessons();
  /** Отчёт последней сдачи анкеты. Пока он есть — вместо формы карточка итога. */
  const [exerciseReport, setExerciseReport] =
    useState<{
      writtenTargets: string[];
      skippedTargets: string[];
      profileCompleteness: number;
    } | null>(null);
  const { markOpened } = useMarkCareerLessonOpened();

  // Fire the «просмотрен» ping exactly once per UNLOCKED lesson. The ref keyed on
  // lesson id survives `cache-and-network` refetches (which replace the `lesson`
  // object but keep its id) so the ping never double-fires, and re-fires only
  // when the student actually opens a different lesson (spec 0020 Фаза B).
  const openedPingRef = useRef<string | null>(null);

  useEffect(() => {
    // 🔴 Это ЗАПИСЬ, хоть и не кнопка: обход по кнопкам её и пропустил.
    // Вовлечённость живёт на студенте, у смотрящего её нет, и сервер отбивает
    // каждый вызов — то есть на каждый открытый урок уходил молчаливый отказ.
    if (!lesson || !lesson.isUnlocked || isPreview) {
      return;
    }
    if (openedPingRef.current === lesson.id) {
      return;
    }
    openedPingRef.current = lesson.id;
    markOpened(lesson.id);
  }, [lesson, markOpened, isPreview]);

  // Markdown authored in the ERP → HTML → allowlist-sanitized before it is
  // injected (spec 0020 — the same `marked` pipeline the regular lesson uses,
  // minus the turndown step, since career content is already Markdown source).
  const safeContent = useMemo(() => {
    if (!lesson?.content) {
      return '';
    }
    const rendered = marked.parse(lesson.content, { async: false }) as string;
    return sanitizeRichText(rendered);
  }, [lesson?.content]);

  const onCompleted = useCallback(() => {
    // The mutation already refetched the list + progress; this re-reads THIS
    // lesson so its own completed state (and the ✓) updates in place.
    refetch();
  }, [refetch]);

  // Блок объяснения — ПЕРЕД разбором загрузки и ошибок: у закрытого раздела
  // запрос не уходил вовсе, и `!lesson` здесь означал бы «не найден», то есть
  // соврал бы про причину.
  if (locked || pending) {
    return (
      <PageWrapper>
        {locked ? <CareerLockedSection capability="lessons" /> : <Spin size="large" />}
      </PageWrapper>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message={t('careerCenter.lesson.loadError')} />;
  }

  if (loading && !lesson) {
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );
  }

  if (!lesson) {
    return <Alert type="warning" showIcon message={t('careerCenter.lesson.notFound')} />;
  }

  const allSections: { id: string; label: string; icon: CareerIconName; show: boolean }[] = [
    {
      id: 'lesson-material',
      label: t('careerCenter.lesson.toc.material'),
      icon: 'learning',
      show: true,
    },
    {
      id: 'lesson-exercise',
      label: t('careerCenter.lesson.toc.exercise'),
      icon: 'clipboard',
      show: Boolean(lesson.exercise) && !isPreview,
    },
    {
      id: 'lesson-quiz',
      label: t('careerCenter.lesson.toc.quiz'),
      icon: 'quiz',
      show: lesson.quizQuestions.length > 0 && !isPreview,
    },
    {
      id: 'lesson-notes',
      label: t('careerCenter.lesson.toc.notes'),
      icon: 'bookmark',
      show: !isPreview,
    },
    {
      id: 'lesson-finish',
      label: t('careerCenter.lesson.toc.finish'),
      icon: 'check',
      show: !isPreview,
    },
  ];
  const sections = allSections.filter((section) => section.show);
  const state = STATE_COPY[lesson.state] ?? STATE_COPY.locked;
  const language =
    lesson.materialLanguage && lesson.materialLanguage !== SHELL_LANGUAGE
      ? LANGUAGE_LABEL[lesson.materialLanguage]
      : null;
  // Следующий ДОСТУПНЫЙ урок после этого — по состоянию с сервера, без своего
  // правила разблокировки. После завершения сервер уже перечитал список.
  const nextLesson =
    lessons.find(
      (row) =>
        row.order > lesson.order && (row.state === 'not_started' || row.state === 'in_progress'),
    ) ?? null;

  const toc = (
    <Toc aria-label={t('careerCenter.lesson.toc.label')}>
      <TocList>
        {sections.map((section) => (
          <li key={section.id}>
            <TocLink href={`#${section.id}`}>
              <CareerIcon name={section.icon} size={18} />
              {section.label}
            </TocLink>
          </li>
        ))}
      </TocList>
    </Toc>
  );

  return (
    <PageWrapper>
      <BackLink to={PATHS.careerLearning}>
        <CareerIcon name="arrowRight" size={18} />
        {t('careerCenter.lesson.back')}
      </BackLink>

      <LessonHeader>
        <LessonHeaderMain>
          <Eyebrow>{t('careerCenter.learning.lessonNumber', { n: lesson.order })}</Eyebrow>
          <LessonTitle>{lesson.title}</LessonTitle>
          {/* ТЗ 0069/0071: что появится ПОСЛЕ урока — сразу под названием, это
              ответ на «зачем мне этот урок». Виден и на закрытом уроке. */}
          {lesson.outcomeLabel || lesson.outcomeKind ? (
            <Outcome>
              {t('careerCenter.plan.meta.outcome', {
                outcome:
                  lesson.outcomeLabel || t(`careerCenter.learning.outcome.${lesson.outcomeKind}`),
              })}
            </Outcome>
          ) : null}
          <LessonMetaRow>
            <StateTag>
              <CareerIcon name={state.icon} size={16} />
              {t(state.label)}
            </StateTag>
            {lesson.durationMin ? (
              <LessonMeta>
                {t('careerCenter.learning.duration', { minutes: lesson.durationMin })}
              </LessonMeta>
            ) : null}
            {language ? <LessonMeta>{t(language)}</LessonMeta> : null}
            {/* «Прошли N» — без знаменателя и только начиная с порога. */}
            {lesson.peersCompleted ? (
              <LessonMeta>
                {t('careerCenter.lesson.peersCompleted', { n: lesson.peersCompleted })}
              </LessonMeta>
            ) : null}
          </LessonMetaRow>
        </LessonHeaderMain>
        {/* Закладка — вторичная кнопка. В предпросмотре кнопок записи нет:
            сервер их всё равно отобьёт. */}
        {lesson.isUnlocked && !isPreview ? (
          <CareerBookmarkButton lessonId={lesson.id} isBookmarked={lesson.isBookmarked} />
        ) : null}
      </LessonHeader>

      {!lesson.isUnlocked ? (
        <Reading>
          <LessonSummary>{lesson.summary}</LessonSummary>
          <LockedNotice>
            <CareerIcon name="lock" size={28} />
            <div>
              <LockedTitle>{t('careerCenter.lesson.lockedTitle')}</LockedTitle>
              <LockedText>
                {lesson.unlockAfter
                  ? t('careerCenter.learning.unlockAfter', {
                      order: lesson.unlockAfter.order,
                      title: lesson.unlockAfter.title,
                    })
                  : t('careerCenter.lesson.lockedText')}
              </LockedText>
            </div>
          </LockedNotice>
        </Reading>
      ) : (
        <Columns>
          {/* На телефоне оглавление — свёрнутым блоком наверху. */}
          <MobileToc>
            <summary>{t('careerCenter.lesson.toc.label')}</summary>
            {toc}
          </MobileToc>

          <Reading>
            <section id="lesson-material" aria-labelledby="lesson-material-title">
              <SectionTitle id="lesson-material-title">
                <SectionIcon aria-hidden>
                  <CareerIcon name="learning" size={20} />
                </SectionIcon>
                {t('careerCenter.lesson.toc.material')}
              </SectionTitle>
              {lesson.videoUrl ? (
                <VideoWrapper>
                  <VideoPlayer src={lesson.videoUrl} active_role={active_role} />
                </VideoWrapper>
              ) : null}

              {safeContent ? (
                <LessonContent dangerouslySetInnerHTML={{ __html: safeContent }} />
              ) : (
                <LessonSummary>{lesson.summary}</LessonSummary>
              )}

              {lesson.attachments.length ? (
                <AttachmentsBlock>
                  <AttachmentsTitle>{t('careerCenter.lesson.attachments')}</AttachmentsTitle>
                  <AttachmentsList>
                    {lesson.attachments.map((attachment) =>
                      attachment.url ? (
                        <AttachmentLink
                          key={attachment.id}
                          href={attachment.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={attachment.originalName}>
                          <AttachmentIcon>
                            <CareerIcon name="paperclip" size={20} />
                          </AttachmentIcon>
                          <AttachmentName>{attachment.originalName}</AttachmentName>
                          <AttachmentSize>
                            {formatAttachmentSize(attachment.sizeBytes)}
                          </AttachmentSize>
                        </AttachmentLink>
                      ) : null,
                    )}
                  </AttachmentsList>
                </AttachmentsBlock>
              ) : null}
            </section>

            {/* ТЗ 0039 — анкета. Ключи с ОТДЕЛЬНЫМИ префиксами: два соседних узла
                с одинаковым ключом React 17 схлопывает (шрам ТЗ 0020). */}
            {lesson.exercise && !isPreview ? (
              <section id="lesson-exercise">
                <SectionTitle>
                  <SectionIcon aria-hidden>
                    <CareerIcon name="clipboard" size={20} />
                  </SectionIcon>
                  {t('careerCenter.lesson.toc.exercise')}
                </SectionTitle>
                {exerciseReport ? (
                  <CareerExerciseResult
                    key={`exercise-result-${lesson.id}`}
                    outro={lesson.exercise?.outro ?? ''}
                    writtenTargets={exerciseReport.writtenTargets}
                    skippedTargets={exerciseReport.skippedTargets}
                    profileCompleteness={exerciseReport.profileCompleteness}
                  />
                ) : (
                  <CareerExerciseForm
                    key={`exercise-${lesson.id}`}
                    lessonId={lesson.id}
                    exercise={lesson.exercise}
                    isCompletionGate={lesson.completionKind === 'exercise'}
                    onSubmitted={setExerciseReport}
                  />
                )}
              </section>
            ) : null}

            {lesson.quizQuestions.length && !isPreview ? (
              // Ключ по уроку: оценённая попытка не протекает в следующий урок.
              // Префикс `quiz-` обязателен — см. шрам ТЗ 0020 про дубли ключей.
              <section id="lesson-quiz">
                <CareerLessonQuiz
                  key={`quiz-${lesson.id}`}
                  lessonId={lesson.id}
                  questions={lesson.quizQuestions}
                />
              </section>
            ) : null}

            {isPreview ? null : (
              <section id="lesson-notes">
                <CareerLessonNotes
                  key={`notes-${lesson.id}`}
                  lessonId={lesson.id}
                  initialNotes={lesson.myNotes}
                />
              </section>
            )}

            {/* ТЗ 5.3: блок завершения — что выполнить, состояние, действие. */}
            {isPreview ? null : (
              <Finish id="lesson-finish" aria-labelledby="lesson-finish-title">
                <SectionTitle id="lesson-finish-title">
                  <SectionIcon aria-hidden>
                    <CareerIcon name="check" size={20} />
                  </SectionIcon>
                  {t('careerCenter.lesson.toc.finish')}
                </SectionTitle>
                {!lesson.isCompleted ? (
                  <Requirement>
                    {t(REQUIREMENT[lesson.completionKind] ?? REQUIREMENT.acknowledge)}
                  </Requirement>
                ) : null}
                {/* На уроке-анкете кнопки «Ukończ lekcję» нет: засчитывает
                    отправка анкеты, и сервер кнопку всё равно отобьёт. */}
                {lesson.completionKind !== 'exercise' || lesson.isCompleted ? (
                  <CareerLessonComplete
                    lessonId={lesson.id}
                    completionKind={lesson.completionKind}
                    artifactHint={lesson.artifactHint}
                    isCompleted={lesson.isCompleted}
                    completedAt={lesson.completedAt}
                    onCompleted={onCompleted}
                  />
                ) : null}
                {lesson.isCompleted && nextLesson ? (
                  <CareerLinkCard to={lessonHref(nextLesson.id)} tone="current">
                    <NextLabel>{t('careerCenter.learning.nextTitle')}</NextLabel>
                    <NextTitle>
                      {t('careerCenter.learning.lessonNumber', { n: nextLesson.order })} ·{' '}
                      {nextLesson.title}
                    </NextTitle>
                  </CareerLinkCard>
                ) : null}
              </Finish>
            )}
          </Reading>

          <DesktopToc>{toc}</DesktopToc>
        </Columns>
      )}
    </PageWrapper>
  );
});

const STATE_COPY: Record<string, { label: string; icon: CareerIconName }> = {
  not_started: { label: 'careerCenter.learning.state.not_started', icon: 'arrowRight' },
  in_progress: { label: 'careerCenter.learning.state.in_progress', icon: 'clock' },
  completed: { label: 'careerCenter.learning.state.completed', icon: 'check' },
  locked: { label: 'careerCenter.learning.state.locked', icon: 'lock' },
};

const LANGUAGE_LABEL: Record<string, string> = {
  pl: 'careerCenter.learning.language.pl',
  ru: 'careerCenter.learning.language.ru',
  en: 'careerCenter.learning.language.en',
};

const REQUIREMENT: Record<string, string> = {
  acknowledge: 'careerCenter.lesson.requirement.acknowledge',
  artifact: 'careerCenter.lesson.requirement.artifact',
  exercise: 'careerCenter.lesson.requirement.exercise',
};

const SHELL_LANGUAGE = appLanguage();

const PageWrapper = styled.div`
  width: 100%;
  /* Текст 760 + оглавление 240 + зазор: в широкой оболочке раздела (15.09)
     кнопка закладки и оглавление иначе уезжали к правому краю окна. */
  max-width: ${careerLayout.readingWidth + careerLayout.sectionGap + 240}px;
  padding-top: 24px;
  margin-bottom: 80px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  color: ${Colors.black};
  ${careerType.bodyStrong()};

  svg {
    transform: rotate(180deg);
  }

  &:hover {
    color: ${Colors.black};
    text-decoration: underline;
  }
`;

const LessonHeader = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 16px 0 ${careerLayout.sectionGap}px;
`;

const LessonHeaderMain = styled.div`
  flex: 1 1 480px;
  min-width: 0;
  max-width: ${careerLayout.readingWidth}px;
`;

const Eyebrow = styled.p`
  margin: 0 0 4px;
  color: ${Colors.gray_dark};
  ${careerType.metaStrong()};
`;

const LessonTitle = styled.h1`
  margin: 0 0 8px;
  ${careerType.h1()};
`;

const Outcome = styled.p`
  margin: 0 0 8px;
  ${careerType.bodyStrong()};
`;

const LessonMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
`;

const StateTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${careerType.metaStrong()};
`;

const LessonMeta = styled.span`
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${careerLayout.sectionGap}px;

  ${desktop} {
    grid-template-columns: minmax(0, ${careerLayout.readingWidth}px) 240px;
    justify-content: space-between;
  }
`;

const Reading = styled.div`
  min-width: 0;
  max-width: ${careerLayout.readingWidth}px;
  display: flex;
  flex-direction: column;
  gap: ${careerLayout.sectionGap}px;
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
  ${careerType.h2()};
`;

/** Знак секции в цвете обучения — тот же, что у пункта оглавления. */
const SectionIcon = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${accent('learning').bg};
  color: ${accent('learning').fg};
`;

const Toc = styled.nav``;

const TocList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TocLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border-left: 3px solid ${accent('learning').bg};
  color: ${Colors.black};
  ${careerType.body()};

  svg {
    color: ${accent('learning').fg};
  }

  &:hover {
    color: ${Colors.black};
    border-left-color: ${accent('learning').fg};
    background: ${Colors.bg_gray};
  }
`;
const DesktopToc = styled.aside`
  display: none;

  ${desktop} {
    display: block;
    position: sticky;
    top: 24px;
    align-self: start;
  }
`;

const MobileToc = styled.details`
  ${careerType.bodyStrong()};

  summary {
    display: flex;
    align-items: center;
    min-height: ${careerLayout.hitTarget}px;
    cursor: pointer;
  }

  ${desktop} {
    display: none;
  }
`;

const LessonSummary = styled.p`
  margin: 0;
  ${careerType.lesson()};
`;

const VideoWrapper = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const LessonContent = styled.div`
  width: 100%;
  ${careerType.lesson()};

  h1,
  h2,
  h3,
  h4 {
    margin: 32px 0 12px;
    ${careerType.h3()};
  }

  p {
    margin: 0 0 16px;
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
  }

  a {
    /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом — правило в colors.ts. */
    color: ${Colors.black};
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  pre {
    padding: 16px;
    overflow-x: auto;
    border-radius: 8px;
    background-color: ${Colors.bg_gray};
  }

  /* ТЗ 5.3: таблица подстраивается под колонку, а широкая прокручивается
     внутри себя — страница горизонтально не прокручивается никогда. */
  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }

  td,
  th {
    padding: 8px 12px;
    border: 1px solid ${Colors.system_gray};
  }
`;

const LockedNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: ${careerLayout.cardPadding}px;
  border: 1px dashed ${Colors.system_gray};
  border-radius: ${careerLayout.cardRadius}px;
  background-color: ${Colors.bg_gray};
`;

const LockedTitle = styled.div`
  ${careerType.bodyStrong()};
`;

const LockedText = styled.div`
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const AttachmentsBlock = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const AttachmentsTitle = styled.h3`
  margin: 0 0 12px;
  ${careerType.h3()};
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AttachmentLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: ${careerLayout.primaryButton}px;
  padding: 12px 16px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  color: ${Colors.black};
  text-decoration: none;

  &:hover {
    color: ${Colors.black};
    border-color: ${Colors.black};
  }
`;

const AttachmentIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
`;

const AttachmentName = styled.span`
  flex-grow: 1;
  word-break: break-word;
  ${careerType.bodyStrong()};
`;

const AttachmentSize = styled.span`
  flex-shrink: 0;
  color: ${Colors.gray_dark};
  ${careerType.meta()};
`;

const Finish = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${careerLayout.cardPadding}px;
  border-radius: ${careerLayout.cardRadius}px;
  border: 1px solid ${Colors.system_gray};
`;

const Requirement = styled.p`
  margin: 0;
  ${careerType.body()};
`;

const NextLabel = styled.span`
  display: block;
  color: ${Colors.gray_dark};
  ${careerType.metaStrong()};
`;

const NextTitle = styled.span`
  display: block;
  ${careerType.h3()};
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
